import React, { useEffect, useState, useContext } from 'react';
import Select from 'react-select';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from "../UserContext";
import axios from 'axios';
import Header from '../Header'; // assuming you have a shared Header component
import './index.css';   // style this file for table responsiveness

const CustomersList = () => {
  const { user, token } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('all');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); 
// values like: all, vr_active, vr_inactive, ott1_active, etc.
  const [sortByExpiryAsc, setSortByExpiryAsc] = useState(false);

  const navigate = useNavigate();
  
  const role = user.role;

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setShowLoading(true);
      const response = await axios.get(`https://vrplay.in/portal_php/allCustomers.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });      
      setCustomers(response.data || []);
      setFilteredCustomers(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
      setShowLoading(false);
    }
  };

  const fetchOperators = async () => {
      try {
        const response = await fetch(`https://vrplay.in/portal_php/operator_api.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const data = await response.json();        
        if (response.ok) {
          setOperators(data.operators);
          setError(null);
        } else {
          setError(data.error || 'Failed to fetch operators');
        }
      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
  

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (role !== "operator") {
      fetchOperators();
    }
  }, [role]);

  // Debounced Search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!searchTerm) {
        setFilteredCustomers(customers);
        return;
      }
      const term = searchTerm.toLowerCase();
      const results = customers.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
      setFilteredCustomers(results);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, customers]);

  const isAnyPlanActive = (cust) => {
    const today = new Date();

    const dates = [
      cust.ExpiryDate,
      cust.ottplay_expiry1,
      cust.playboxtv_expiry,
      cust.watcho_expiry
    ];

    return dates.some(date => {
      if (!date || date === "0000-00-00") return false;
      return new Date(date) >= today;
    });
  };

  useEffect(() => {
    let list = [...customers];

    if (filterStatus === 'vr_active') {
      list = list.filter(isVRActive);
    } else if (filterStatus === 'vr_inactive') {
      list = list.filter(c => !isVRActive(c));
    } else if (filterStatus === 'ott1_active') {
      list = list.filter(isOTT1Active);
    } else if (filterStatus === 'ott1_inactive') {
      list = list.filter(c => !isOTT1Active(c));
    } else if (filterStatus === 'ott2_active') {
      list = list.filter(isPlayboxtvActive);
    } else if (filterStatus === 'ott2_inactive') {
      list = list.filter(c => !isPlayboxtvActive(c));
    } else if (filterStatus === 'watcho_active') {
      list = list.filter(isWatchoActive);
    } else if (filterStatus === 'watcho_inactive') {
      list = list.filter(c => !isWatchoActive(c));
    }
    if (selectedOperatorId !== 'all') {
      list = list.filter(c => c.operatorId === selectedOperatorId);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
    }
    
    setFilteredCustomers(list);
  }, [searchTerm, customers, filterStatus, selectedOperatorId]);

  useEffect(() => {
    let list = [...filteredCustomers];
    
    // ✅ Sort by ExpiryDate
    list.sort((a, b) => {
      const dateA = new Date(a.ExpiryDate);
      const dateB = new Date(b.ExpiryDate);
      return sortByExpiryAsc ? dateA - dateB : dateB - dateA;
    });

    setFilteredCustomers(list);
  }, [sortByExpiryAsc]);

  const operatorOptions = [
    { value: 'all', label: 'All Operators' },

    ...(role === "admin"
      ? [{
          value: user.operatorId,
          label: `${user.phoneNumber || user.operatorPhoneNumber || ''} - ${user.operatorName} (Admin)`
        }]
      : []),

    ...operators.map(op => ({
      value: op.id,
      label: `${op.phoneNumber || ''} - ${op.operatorName}`
    }))
  ];

  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  const formatExpiry = (date) => {
    if (!date || date === "0000-00-00") return "N/A";
    return date;
  };

  const isPlanActive = (date) => {
    if (!date || date === "0000-00-00") return false;
    return new Date(date) >= new Date();
  };

  const isVRActive = (c) => isPlanActive(c.ExpiryDate);
  const isOTT1Active = (c) => isPlanActive(c.ottplay_expiry1);
  const isPlayboxtvActive = (c) => isPlanActive(c.playboxtv_expiry);
  const isWatchoActive = (c) => isPlanActive(c.watcho_expiry);

  return (
    <>
      <Header />

      <div className="operator-header">
        <h2>Customers</h2>
        {role !== "operator" && (
          <Select
            options={operatorOptions}
            value={
              operatorOptions.find(op => op.value === selectedOperatorId) 
              || operatorOptions[0]
            }
            onChange={(selected) =>
              setSelectedOperatorId(
                selected.value === 'all' ? 'all' : Number(selected.value)
              )
            }
            placeholder="Search by phone or name..."
            isSearchable
            styles={{
              container: (base) => ({
                ...base,
                minWidth: 250,   // ✅ FIXED WIDTH
                width: 250       // optional fixed width
              })
            }}
          />
        )}

        <input
          type="text"
          className="search-input"
          placeholder="Search by name or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="add-operator-btn" onClick={() => navigate('/add-customer')}>
          + Add Customer
        </button>
      </div>


      <div className="customer-summary">
        <div className="customer-summary">
          <strong>Total: {customers.length}</strong> |{" "}
          <span>VRPLAY: {customers.filter(isVRActive).length}</span> |{" "}
          <span>OTT1: {customers.filter(isOTT1Active).length}</span> |{" "}
          <span>Playboxtv: {customers.filter(isPlayboxtvActive).length}</span> |{" "}
          <span>Watcho: {customers.filter(isWatchoActive).length}</span>
        </div>
        <div className="filter-section">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="search-input"
          >
            <option value="all">All</option>
            <option value="vr_active">VRPLAY Active</option>
            <option value="vr_inactive">VRPLAY Inactive</option>
            <option value="ott1_active">OTTPlay1 Active</option>
            <option value="ott1_inactive">OTTPlay1 Inactive</option>
            <option value="ott2_active">Playbox TV Active</option>
            <option value="ott2_inactive">Playbox TV Inactive</option>
            <option value="watcho_active">Watcho Active</option>
            <option value="watcho_inactive">Watcho Inactive</option>
          </select>
        </div>
      </div>

        {showLoading || loading ? (
        <div className="loading-container">
          <div className="skeleton-loader">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="skeleton-row">
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
          <p className="error">{error}</p>
        ) : (
          <div className="operator-list table-wrapper">
            {filteredCustomers.length > 0 ? (
              <table className="customers-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th onClick={() => setSortByExpiryAsc(prev => !prev)} style={{ cursor: 'pointer' }}>
                      VRPLAY Expiry {sortByExpiryAsc ? '▲' : '▼'}
                    </th>
                    <th onClick={() => setSortByExpiryAsc(prev => !prev)} style={{ cursor: 'pointer' }}>
                      OTTPLAY1 Expiry {sortByExpiryAsc ? '▲' : '▼'}
                    </th>
                    <th onClick={() => setSortByExpiryAsc(prev => !prev)} style={{ cursor: 'pointer' }}>
                      Watcho Expiry {sortByExpiryAsc ? '▲' : '▼'}
                    </th>
                    <th onClick={() => setSortByExpiryAsc(prev => !prev)} style={{ cursor: 'pointer' }}>
                      Playbox TV Expiry {sortByExpiryAsc ? '▲' : '▼'}
                    </th>
                    {user.role !== "operator" && <th>Operator</th>}
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((cust, index) => (
                    <tr key={cust.id} className={isAnyPlanActive(cust) ? 'row-active' : 'row-inactive'}>
                      <td>{index + 1}</td>
                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.username, searchTerm),
                            }}
                          />
                        </Link>
                      </td>

                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.name + " " + cust.lastName, searchTerm),
                            }}
                          />
                        </Link>
                      </td>
                      <td>{cust.MobileNumber}</td>
                      <td className={isPlanActive(cust.ExpiryDate) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.ExpiryDate)}</td>
                      <td className={isPlanActive(cust.ottplay_expiry1) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.ottplay_expiry1)}</td>
                      <td className={isPlanActive(cust.watcho_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.watcho_expiry)}</td>
                      <td className={isPlanActive(cust.playboxtv_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.playboxtv_expiry)}</td>
                      {user.role !== "operator" && <td>{cust.operatorDetails}</td>}
                      <td>{cust.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No customers found.</p>
            )}
          </div>
        )}
    </>
  );
};

export default CustomersList;
